import { useState, useMemo } from 'react';
import {
  Search,
  Filter,
  Users,
  BarChart3,
  TrendingUp,
  DollarSign,
  PiggyBank,
  UserCheck,
  RefreshCw,
  Calendar,
  Sparkles,
  Award,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@medikei/ui';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/providers/ToastProvider';
import { PartnerTable } from '../components/PartnerTable';
import {
  useAdminPartners,
  useApprovePartner,
  useRejectPartner,
  useSuspendPartner,
  useActivatePartner,
} from '../hooks/useAdminPartners';
import { useAdminPartnerAnalytics } from '../hooks/useAdminPartnerAnalytics';
import {
  ExecutiveMetricCard,
  PartnerFunnelChart,
  PartnerFinancialPerformanceChart,
  PartnerCacComparisonChart,
} from '../components/analytics';

type PeriodOption = '7d' | '30d' | '90d' | '12m';

export default function AdminPartnersPage() {
  const { toast } = useToast();

  // Estados de navegação e filtros globais de analytics
  const [activeTab, setActiveTab] = useState<'analytics' | 'management'>('analytics');
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodOption>('30d');
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('ALL');
  const [financialGroupBy, setFinancialGroupBy] = useState<'DAY' | 'MONTH'>('DAY');

  // Estados da aba de parceiros
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [kycFilter, setKycFilter] = useState<string>('ALL');
  const [levelFilter, setLevelFilter] = useState<string>('ALL');

  // Cálculo das datas com base no período selecionado
  const dateRange = useMemo(() => {
    const end = new Date();
    const start = new Date();

    if (selectedPeriod === '7d') {
      start.setDate(end.getDate() - 7);
    } else if (selectedPeriod === '30d') {
      start.setDate(end.getDate() - 30);
    } else if (selectedPeriod === '90d') {
      start.setDate(end.getDate() - 90);
    } else if (selectedPeriod === '12m') {
      start.setFullYear(end.getFullYear() - 1);
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, [selectedPeriod]);

  // Hook de Analytics
  const {
    overview,
    isLoadingOverview,
    funnel,
    isLoadingFunnel,
    financialTrend,
    isLoadingFinancialTrend,
    cacComparison,
    isLoadingCacComparison,
    isLoadingAny: isLoadingAnalytics,
    refetchOverview,
    refetchFunnel,
    refetchFinancialTrend,
    refetchCacComparison,
  } = useAdminPartnerAnalytics({
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    serviceType: serviceTypeFilter !== 'ALL' ? serviceTypeFilter : undefined,
    groupBy: financialGroupBy,
  });

  // Hook de Parceiros
  const { data, isLoading } = useAdminPartners({
    page,
    limit: 10,
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    kycStatus: kycFilter !== 'ALL' ? kycFilter : undefined,
    level: levelFilter !== 'ALL' ? levelFilter : undefined,
    search: search || undefined,
  });

  const approveMutation = useApprovePartner();
  const rejectMutation = useRejectPartner();
  const suspendMutation = useSuspendPartner();
  const activateMutation = useActivatePartner();

  const handleRefreshAnalytics = () => {
    refetchOverview();
    refetchFunnel();
    refetchFinancialTrend();
    refetchCacComparison();
    toast({ title: 'Dados de analytics atualizados', variant: 'success' });
  };

  const handleApprove = async (id: string) => {
    try {
      await approveMutation.mutateAsync(id);
      toast({ title: 'Parceiro aprovado com sucesso!', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao aprovar parceiro', variant: 'error' });
    }
  };

  const handleReject = async (id: string) => {
    try {
      await rejectMutation.mutateAsync({ id });
      toast({ title: 'Parceiro rejeitado', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao rejeitar parceiro', variant: 'error' });
    }
  };

  const handleSuspend = async (id: string) => {
    try {
      await suspendMutation.mutateAsync({ id });
      toast({ title: 'Parceiro suspenso', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao suspender parceiro', variant: 'error' });
    }
  };

  const handleActivate = async (id: string) => {
    try {
      await activateMutation.mutateAsync(id);
      toast({ title: 'Parceiro ativado com sucesso!', variant: 'success' });
    } catch {
      toast({ title: 'Erro ao ativar parceiro', variant: 'error' });
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      {/* Header Principal com Título e Ações */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-foreground">
                Programa de Parceiros & Afiliados
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Dashboard executivo financeiro, funil de aquisição ponta a ponta e controle de parceiros
              </p>
            </div>
          </div>
        </div>

        {/* Filtros Globais de Analytics */}
        <div className="flex items-center flex-wrap gap-2.5">
          <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg p-1 shadow-xs">
            <Calendar className="h-4 w-4 text-muted-foreground ml-2" />
            <Select
              value={selectedPeriod}
              onValueChange={(val) => {
                setSelectedPeriod(val as PeriodOption);
                if (val === '12m') setFinancialGroupBy('MONTH');
              }}
            >
              <SelectTrigger className="w-[145px] h-8 text-xs border-0 shadow-none focus:ring-0">
                <SelectValue placeholder="Período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7d">Últimos 7 dias</SelectItem>
                <SelectItem value="30d">Últimos 30 dias</SelectItem>
                <SelectItem value="90d">Últimos 90 dias</SelectItem>
                <SelectItem value="12m">Últimos 12 meses</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-1.5 bg-card border border-border rounded-lg p-1 shadow-xs">
            <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
              <SelectTrigger className="w-[145px] h-8 text-xs border-0 shadow-none focus:ring-0">
                <SelectValue placeholder="Serviço" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos Serviços</SelectItem>
                <SelectItem value="TELEMEDICINA">Telemedicina</SelectItem>
                <SelectItem value="DOMICILIAR">Domiciliar</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAnalytics}
            disabled={isLoadingAnalytics}
            className="h-10 px-3 text-xs flex items-center gap-1.5"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${isLoadingAnalytics ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Atualizar</span>
          </Button>
        </div>
      </div>

      {/* Navegação por Abas */}
      <Tabs
        value={activeTab}
        onValueChange={(val) => setActiveTab(val as 'analytics' | 'management')}
        className="w-full"
      >
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Visão Geral & Analytics
          </TabsTrigger>
          <TabsTrigger value="management" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Gerenciamento ({data?.meta?.total ?? (data as any)?.total ?? 0})
          </TabsTrigger>
        </TabsList>

        {/* ABA 1: VISÃO GERAL & ANALYTICS */}
        <TabsContent value="analytics" className="space-y-6 mt-6">
          {/* Grid de Cards Executivos de KPI (8 KPIs Principais) */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* 1. GMV Total */}
            <ExecutiveMetricCard
              title="GMV Total (Consultas)"
              value={overview?.totalGmv ?? 0}
              isCurrency={true}
              icon={DollarSign}
              iconColorClass="text-blue-600 dark:text-blue-400"
              iconBgClass="bg-blue-50 dark:bg-blue-950/40"
              badge={{ text: 'Volume', variant: 'info' }}
              subtitle="Receita bruta transacionada por afiliados"
              infoTooltip="Volume Bruto: Soma total de todo o dinheiro pago pelos pacientes em consultas agendadas através dos links e indicações de parceiros no período."
              loading={isLoadingOverview}
            />

            {/* 2. Comissões Líquidas */}
            <ExecutiveMetricCard
              title="Comissões Aprovadas"
              value={overview?.netCommissions ?? 0}
              isCurrency={true}
              icon={TrendingUp}
              iconColorClass="text-emerald-600 dark:text-emerald-400"
              iconBgClass="bg-emerald-50 dark:bg-emerald-950/40"
              badge={{
                text: `${overview?.takeRatePercent ?? 0}% take-rate`,
                variant: 'success',
              }}
              subtitle="Total provisionado aos parceiros"
              infoTooltip="Comissões Validadas: Total de bonificações aprovadas pela plataforma para pagamento aos parceiros (12% na 1ª consulta e 5% na recorrência por 6 meses)."
              loading={isLoadingOverview}
            />

            {/* 3. Comissões Pagas */}
            <ExecutiveMetricCard
              title="Comissões Já Pagas"
              value={overview?.paidCommissions ?? 0}
              isCurrency={true}
              icon={Award}
              iconColorClass="text-amber-600 dark:text-amber-400"
              iconBgClass="bg-amber-50 dark:bg-amber-950/40"
              badge={{ text: 'Liquidado', variant: 'warning' }}
              subtitle="Payouts PIX já transferidos"
              infoTooltip="Comissões Liquidadas: Total em dinheiro que já foi transferido para a chave PIX ou conta dos parceiros e pacientes que indicaram."
              loading={isLoadingOverview}
            />

            {/* 4. Economia Financeira CAC */}
            <ExecutiveMetricCard
              title="Economia Total de CAC"
              value={overview?.cacSavings ?? 0}
              isCurrency={true}
              icon={PiggyBank}
              iconColorClass="text-purple-600 dark:text-purple-400"
              iconBgClass="bg-purple-50 dark:bg-purple-950/40"
              badge={{ text: 'Eficiência', variant: 'purple' }}
              subtitle="Economia vs aquisição orgânica/mídia"
              infoTooltip="Economia de Marketing: Quanto a Medikei economizou trazendo pacientes por indicações orgânicas em vez de pagar anúncios caros de Google/Facebook (benchmark de R$ 45 por paciente)."
              loading={isLoadingOverview}
            />

            {/* 5. Novos Pacientes / Signups */}
            <ExecutiveMetricCard
              title="Novos Pacientes Indicados"
              value={overview?.totalSignups?.toLocaleString('pt-BR') ?? '0'}
              icon={Users}
              iconColorClass="text-indigo-600 dark:text-indigo-400"
              iconBgClass="bg-indigo-50 dark:bg-indigo-950/40"
              subtitle={`${overview?.totalFirstConsultations ?? 0} consultas ativadas`}
              infoTooltip="Novos Cadastros: Quantidade de pessoas que criaram conta na plataforma após clicarem no link de um parceiro ou paciente amigo."
              loading={isLoadingOverview}
            />

            {/* 6. Parceiros Ativos */}
            <ExecutiveMetricCard
              title="Parceiros Ativos com Conversão"
              value={overview?.activePartners ?? 0}
              icon={UserCheck}
              iconColorClass="text-teal-600 dark:text-teal-400"
              iconBgClass="bg-teal-50 dark:bg-teal-950/40"
              subtitle="Gerando conversões no período"
              infoTooltip="Parceiros Produtivos: Quantidade de parceiros ou pacientes cujo link gerou pelo menos uma consulta realizada no período selecionado."
              loading={isLoadingOverview}
            />

            {/* 7. CAC Médio de Parceiro */}
            <ExecutiveMetricCard
              title="CAC Médio Parceiros"
              value={overview?.partnerCac ?? 0}
              isCurrency={true}
              icon={Sparkles}
              iconColorClass="text-emerald-600 dark:text-emerald-400"
              iconBgClass="bg-emerald-50 dark:bg-emerald-950/40"
              subtitle={`vs ${overview?.organicCac ? `R$ ${overview.organicCac.toFixed(2)}` : 'R$ 45,00'} canal direto`}
              badge={{ text: '-51% CAC', variant: 'success' }}
              infoTooltip="Custo por Paciente: Valor médio de comissão pago pela plataforma para conquistar cada novo paciente (em torno de R$ 22), sendo mais de 50% mais barato que anúncios."
              loading={isLoadingOverview}
            />

            {/* 8. Take-Rate Efetivo */}
            <ExecutiveMetricCard
              title="Take-Rate do Programa"
              value={`${overview?.takeRatePercent ?? 0}%`}
              icon={BarChart3}
              iconColorClass="text-rose-600 dark:text-rose-400"
              iconBgClass="bg-rose-50 dark:bg-rose-950/40"
              subtitle="Custo de comissão sobre o GMV"
              infoTooltip="Percentual de Repasse: Porcentagem média do valor das consultas que é repassada como comissão para os afiliados (taxa de remuneração da rede)."
              loading={isLoadingOverview}
            />
          </div>

          {/* Gráficos Principais: Funil & Desempenho Financeiro */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Funil de Conversão (5 colunas) */}
            <div className="lg:col-span-5">
              <PartnerFunnelChart funnel={funnel} isLoading={isLoadingFunnel} />
            </div>

            {/* Desempenho Financeiro GMV vs Comissões (7 colunas) */}
            <div className="lg:col-span-7">
              <PartnerFinancialPerformanceChart
                data={financialTrend}
                isLoading={isLoadingFinancialTrend}
                currentGroupBy={financialGroupBy}
                onGroupByChange={setFinancialGroupBy}
              />
            </div>
          </div>

          {/* Comparativo de CAC & Eficiência de Aquisição */}
          <div>
            <PartnerCacComparisonChart
              data={cacComparison}
              isLoading={isLoadingCacComparison}
            />
          </div>
        </TabsContent>

        {/* ABA 2: GERENCIAMENTO DE PARCEIROS */}
        <TabsContent value="management" className="space-y-6 mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Filtros de Busca de Parceiros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por nome, e-mail ou CPF/CNPJ..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-10"
                  />
                </div>

                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos Status</SelectItem>
                    <SelectItem value="PENDING">Pendente</SelectItem>
                    <SelectItem value="APPROVED">Aprovado</SelectItem>
                    <SelectItem value="REJECTED">Rejeitado</SelectItem>
                    <SelectItem value="SUSPENDED">Suspenso</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={kycFilter} onValueChange={setKycFilter}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="KYC" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos KYC</SelectItem>
                    <SelectItem value="PENDING">Pendente</SelectItem>
                    <SelectItem value="VERIFIED">Verificado</SelectItem>
                    <SelectItem value="REJECTED">Rejeitado</SelectItem>
                    <SelectItem value="NOT_SUBMITTED">Não Enviado</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={levelFilter} onValueChange={setLevelFilter}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Nível" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Todos Níveis</SelectItem>
                    <SelectItem value="BRONZE">Bronze</SelectItem>
                    <SelectItem value="SILVER">Prata</SelectItem>
                    <SelectItem value="GOLD">Ouro</SelectItem>
                    <SelectItem value="PLATINUM">Platina</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          <PartnerTable
            partners={data?.data || (data as any)?.items || []}
            isLoading={isLoading}
            total={data?.meta?.total ?? (data as any)?.total ?? 0}
            page={page}
            pageSize={data?.meta?.perPage ?? (data as any)?.limit ?? 10}
            onPageChange={setPage}
            onApprove={handleApprove}
            onReject={handleReject}
            onSuspend={handleSuspend}
            onActivate={handleActivate}
            isApproving={approveMutation.isPending}
            isRejecting={rejectMutation.isPending}
            isSuspending={suspendMutation.isPending}
            isActivating={activateMutation.isPending}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
